import { useState, useMemo, useCallback, useRef, memo } from 'react';
import styled from '@emotion/styled';
import { theme, scaled } from '../styles/theme';
import { Card, ChartCardTitle, ToggleGroup, ToggleButton } from '../styles/components';
import { getTypeColor, ALL_INVESTMENT_TYPES, PHYSICAL_TYPES, FINANCIAL_TYPES } from '../utils/colors';
import { formatInvestmentVolume } from '../utils/formatters';
import { exportRankedBarChartImage } from '../utils/chartImageExport';
import { otherLabel, typeLabel, useT } from '../i18n';

type Mode = 'count' | 'volume';

// One row of the source aggregate: a category (country / industry) split by
// investment type. `label` is the category; the chart sums across types.
export interface RankedDatum {
  label: string;
  type: string;
  count: number;
  volume: number;
}

interface BreadcrumbSegment {
  label: string;
  /** Non-terminal segments get an onClick; the current/terminal segment omits
      it and renders as plain text. */
  onClick?: () => void;
}

interface Props {
  title: string;
  data: RankedDatum[];
  /** category label -> cluster ids behind it, for table/map drill-down.
      Used by the country chart; the industry chart drills via the industries
      filter instead and passes an empty map. */
  clusterIds: Record<string, number[]>;
  selected: string | null;
  // `originals` are the source category labels merged into the clicked row
  // (one for a normal bar, many for the aggregated "Sonstige" bar).
  onSelect: (label: string, clusterIds: number[], originals: string[]) => void;
  investmentTypeFilter: string[];
  activeTiers: { physical: boolean; financial: boolean };
  onTypeFilterChange: (types: string[]) => void;
  topN?: number;
  /** Label for the leftover bucket when there are more than topN categories.
      A data key (it comes back through onSelect) — displayed via formatLabel. */
  restLabel?: string;
  /** Display form of a category label (and breadcrumb segment); the raw label
      stays the key for selection. Defaults to otherLabel ("Sonstige" → "Other"). */
  formatLabel?: (label: string) => string;
  /** Optional drill breadcrumb shown above the bars. Empty or undefined hides
      the row. Used by the industry chart to expose the active drill path. */
  breadcrumb?: BreadcrumbSegment[];
  /** Labels whose rows have ≥ 2 deeper-level children — i.e. clicking the bar
      would drill the chart one level further. Used to render a chevron cue
      *only* on those rows, so "no chevron = no drill" stays a reliable rule. */
  drillableLabels?: Set<string>;
  /** Single-line invitation rendered in the breadcrumb's slot when no drill
      is active. Disappears the moment the user drills (the breadcrumb takes
      its place). Provide when the chart supports drilling at all. */
  hint?: string;
}

const ChartCard = styled(Card)`
  display: flex;
  flex-direction: column;
  min-height: 360px;
  /* Flat on the page background — no tile shadow. */
  box-shadow: none;
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.md};
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

// Same affordance as the table header's and the map's export trigger:
// bordered square download-icon button. Here it downloads the chart as a
// PNG in its current state.
const ExportButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  font-size: 18px;
  padding: 0;
  /* Clip the icon-font ligature fallback, see the table's ExportButton. */
  overflow: hidden;
  border: 1px solid ${theme.colors.border};
  background: transparent;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  transition: all 0.15s ease;

  &:hover {
    background: rgba(24, 28, 68, 0.05);
    border-color: ${theme.colors.text.secondary};
    color: ${theme.colors.text.primary};
  }
`;

// Sits directly under the header so the breadcrumb reads as part of the
// chart's title block. Muted text, with the colour-data tint on clickable
// segments to invite a click without competing with the bars.
const BreadcrumbRow = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.muted};
  margin-top: -${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
`;

const CrumbLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: var(--color-data);
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const CrumbCurrent = styled.span`
  color: ${theme.colors.text.primary};
  font-weight: ${theme.fontWeight.medium};
`;

const CrumbSep = styled.span`
  color: ${theme.colors.text.muted};
`;

// First-encounter caption. Lives in the breadcrumb's slot so the two never
// stack — once the user drills, the breadcrumb replaces this. Serif italic
// borrows the chart-title face, reading as an editorial caption rather than
// a UI tooltip.
const Hint = styled.p`
  margin: -${theme.spacing.sm} 0 ${theme.spacing.md} 0;
  font-family: var(--serif-stack);
  font-style: italic;
  font-size: ${theme.fontSize.xs};
  line-height: 1.3;
  color: ${theme.colors.text.muted};
  letter-spacing: 0.005em;
`;

const Rows = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: ${theme.spacing.xs};
  padding-top: ${theme.spacing.xs};
`;

// Fixed label/value columns so every bar's track shares the same left and
// right edge (an `auto` value column would size to each row's number and make
// the bars end at ragged x positions). Negative margin lets the hover/selected
// surface bleed to the card padding without shifting the bars.
const Row = styled.div<{ dimmed?: boolean; highlighted?: boolean }>`
  display: grid;
  grid-template-columns: 132px 1fr 100px;
  align-items: center;
  gap: ${theme.spacing.sm};
  cursor: pointer;
  padding: 4px 8px;
  margin: 0 -8px;
  border-radius: 8px;
  opacity: ${p => p.dimmed ? 0.4 : 1};
  background: ${p => p.highlighted ? 'color-mix(in srgb, var(--color-data) 12%, transparent)' : 'transparent'};
  box-shadow: ${p => p.highlighted ? 'inset 2px 0 0 var(--color-data)' : 'none'};
  transition: opacity 0.15s, background 0.15s;
  &:hover {
    opacity: ${p => p.dimmed ? 0.65 : 1};
    background: color-mix(in srgb, var(--color-data) 12%, transparent);
  }

  /* On phones the fixed 132px label + 100px value columns leave the bar
     track barely ~30px wide, so the bars all but vanish and only the values
     read. Stack instead: label + value share the top line, the bar takes the
     full width below — so it actually encodes the ranking visually. */
  @media (max-width: 600px) {
    grid-template-columns: 1fr auto;
    grid-template-areas:
      "label value"
      "bar   bar";
    align-items: baseline;
    column-gap: ${theme.spacing.sm};
    /* Keep rows compact so all ~11 bars fit without a long scroll: tight
       label-to-bar gap and minimal vertical padding. */
    row-gap: 2px;
    padding-top: 4px;
    padding-bottom: 4px;
  }
`;

// Flex shell so the chevron can sit at the right edge of the label cell
// without breaking the text's 2-line clamp. The text child gets min-width:0
// so flex doesn't refuse to shrink it below intrinsic width.
const RowLabel = styled.span`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  overflow: hidden;

  @media (max-width: 600px) {
    grid-area: label;
  }
`;

const RowLabelText = styled.span`
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.primary};
  text-align: left;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
  flex: 1 1 auto;
  min-width: 0;
`;

// Drillable-row affordance. Always-on at low opacity; row hover pulls it
// forward and brightens — the "doorway" gesture. Only rendered when the
// label is in `drillableLabels`, so the cue stays trustworthy: no chevron
// means the click won't change the chart's level.
const RowChevron = styled.span`
  flex: 0 0 auto;
  align-self: center;
  font-family: var(--sans-stack);
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  color: var(--color-data);
  opacity: 0.5;
  transform: translateX(0);
  transition: transform 0.2s ease, opacity 0.2s ease;
  ${Row}:hover & {
    opacity: 0.9;
    transform: translateX(3px);
  }
`;

// Per the DZ bar-chart template: sharp rectangular bars sitting directly on
// the card surface — no grey track, no gridlines behind them.
const Track = styled.div`
  position: relative;
  height: 26px;
  background: transparent;
  border-radius: 2px;
  overflow: hidden;

  @media (max-width: 600px) {
    grid-area: bar;
    /* Shorter bar on mobile — the full-width length still encodes the ranking
       clearly, and a slimmer bar keeps the stacked rows compact. */
    height: 16px;
  }
`;

// The bar's overall length encodes the total (ranking); inside it, coloured
// segments show the investment-type composition. Length animates on changes.
const BarFill = styled.div`
  position: absolute;
  inset: 0 auto 0 0;
  display: flex;
  border-radius: 2px;
  overflow: hidden;
  transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 3px;
`;

// Hairline separators (in the page colour) keep adjacent type segments crisp.
const Seg = styled.div`
  height: 100%;
  flex-shrink: 0;
  &:not(:last-of-type) {
    box-shadow: inset -1px 0 0 var(--color-bg-surface);
  }
`;

// Stable left-to-right segment order so the same type sits in the same place
// across every bar. "Sonstige" always trails.
function typeOrder(t: string): number {
  if (t === 'Other' || t === 'Sonstige') return 999;
  const i = ALL_INVESTMENT_TYPES.indexOf(t);
  return i === -1 ? 998 : i;
}

// Value labels in the sans face, matching the DZ template (the template puts
// them at the bar end; we keep them in a fixed right column so the bars share
// a common right edge, as requested).
const RowValue = styled.span`
  font-family: var(--sans-stack);
  font-size: ${scaled(14)};
  font-weight: ${theme.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-align: left;

  @media (max-width: 600px) {
    grid-area: value;
    text-align: right;
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text.muted};
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.sm};
`;

// Cursor-tracked tooltip (fixed-position) so it never clips against the card.
// Above the sticky filter panel (5) but <= 9 so the host nav stays on top.
//
// Hidden on touch / coarse-pointer devices: it's driven by onMouseMove and
// cleared by onMouseLeave, but a tap fires the synthetic mousemove (showing
// it) while mouseleave never fires until the next tap elsewhere — so on a
// phone it would pop up on tap and stay stuck over the chart. There's no
// cursor to track there anyway, so suppress it entirely and let the tap just
// drill/select. Mouse users keep the tooltip unchanged.
const Tooltip = styled.div`
  position: fixed;
  z-index: 6;
  pointer-events: none;
  background: #fff;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: 10px 14px;
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.sm};
  box-shadow: var(--shadow-m);
  min-width: 180px;
  max-width: 280px;
  transform: translate(12px, 12px);

  @media (hover: none), (pointer: coarse) {
    display: none;
  }
`;
// Long industry names must wrap instead of stretching the tooltip wide.
const tipTitle: React.CSSProperties = {
  fontWeight: theme.fontWeight.semibold,
  marginBottom: 8,
  color: theme.colors.text.primary,
  lineHeight: 1.3,
  overflowWrap: 'break-word',
};
const tipRow: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 16, padding: '2px 0' };
const tipName: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, color: theme.colors.text.secondary };
const tipDot: React.CSSProperties = { width: 10, height: 10, borderRadius: '50%', display: 'inline-block', flexShrink: 0 };

// DZ template legend: white pill chips (radius 19px, 5×9 padding) with a 10px
// round colour dot, wrapping centred below the chart.
const TypeLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  padding-top: 16px;
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.sm};
`;

const TypeLegendItem = styled.span<{ dimmed?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 19px;
  background: #ffffff;
  cursor: pointer;
  color: ${theme.colors.text.primary};
  opacity: ${p => p.dimmed ? 0.4 : 1};
  transition: opacity 0.15s;
  &:hover { opacity: ${p => p.dimmed ? 0.65 : 0.85}; }
`;

const TypeLegendDot = styled.span<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${p => p.color};
`;

interface Aggregated {
  label: string;
  count: number;
  volume: number;
  byType: { type: string; count: number; volume: number }[];
  clusterIds: number[];
  originals: string[]; // labels merged into this row (for "rest")
}

export const RankedBarChart = memo(function RankedBarChart({
  title, data, clusterIds, selected, onSelect,
  investmentTypeFilter, activeTiers, onTypeFilterChange,
  topN = 10, restLabel = 'Sonstige',
  breadcrumb, drillableLabels, hint, formatLabel = otherLabel,
}: Props) {
  const m = useT();
  // Re-render when the English industry names land (formatLabel may use them).
  const [mode, setMode] = useState<Mode>('volume');
  const [tip, setTip] = useState<{ x: number; y: number; row: Aggregated } | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const { rows, max } = useMemo(() => {
    const map = new Map<string, Aggregated>();
    for (const d of data) {
      let agg = map.get(d.label);
      if (!agg) {
        agg = { label: d.label, count: 0, volume: 0, byType: [], clusterIds: clusterIds[d.label] ?? [], originals: [d.label] };
        map.set(d.label, agg);
      }
      agg.count += d.count;
      agg.volume += d.volume;
      agg.byType.push({ type: d.type, count: d.count, volume: d.volume });
    }

    const metric = (a: Aggregated) => mode === 'volume' ? a.volume : a.count;
    const sorted = [...map.values()].sort((a, b) => metric(b) - metric(a));

    let rows = sorted;
    if (sorted.length > topN) {
      const head = sorted.slice(0, topN);
      const tail = sorted.slice(topN);
      const rest: Aggregated = {
        label: restLabel, count: 0, volume: 0, byType: [], clusterIds: [], originals: [],
      };
      const typeAgg = new Map<string, { count: number; volume: number }>();
      for (const t of tail) {
        rest.count += t.count;
        rest.volume += t.volume;
        rest.clusterIds.push(...t.clusterIds);
        rest.originals.push(...t.originals);
        for (const bt of t.byType) {
          const e = typeAgg.get(bt.type) ?? { count: 0, volume: 0 };
          e.count += bt.count; e.volume += bt.volume;
          typeAgg.set(bt.type, e);
        }
      }
      rest.byType = [...typeAgg.entries()].map(([type, v]) => ({ type, ...v }));
      rows = [...head, rest];
    }

    const max = rows.reduce((m, r) => Math.max(m, metric(r)), 0);
    return { rows, max };
  }, [data, clusterIds, mode, topN, restLabel]);

  const handleSelect = useCallback((row: Aggregated) => {
    onSelect(row.label, row.clusterIds, row.originals);
  }, [onSelect]);

  const metricVal = (r: Aggregated) => mode === 'volume' ? r.volume : r.count;
  const fmt = (v: number) => mode === 'volume' ? formatInvestmentVolume(v) : String(v);

  // Types the on-screen legend shows — the exported image redraws the same set.
  const legendTypes = ALL_INVESTMENT_TYPES.filter(t => {
    if (PHYSICAL_TYPES.includes(t)) return activeTiers.physical;
    if (FINANCIAL_TYPES.includes(t)) return activeTiers.financial;
    return false;
  });

  const handleExport = () => {
    const card = cardRef.current;
    if (!card) return;
    exportRankedBarChartImage(card, {
      title,
      modeLabel: mode === 'volume' ? m.charts.volume : m.charts.count,
      breadcrumb: breadcrumb && breadcrumb.length > 0 ? breadcrumb.map(s => formatLabel(s.label)) : undefined,
      rows: rows.map(row => {
        const v = metricVal(row);
        const segs = row.byType
          .map(b => ({ type: b.type, value: mode === 'volume' ? b.volume : b.count }))
          .filter(b => b.value > 0)
          .sort((a, b) => typeOrder(a.type) - typeOrder(b.type));
        return {
          label: formatLabel(row.label),
          value: v,
          valueLabel: fmt(v),
          segments: segs.map(s => ({ color: getTypeColor(s.type), value: s.value })),
          dimmed: selected != null && selected !== row.label,
          highlighted: selected === row.label,
        };
      }),
      maxValue: max,
      legend: legendTypes.map(type => ({
        label: typeLabel(type),
        color: getTypeColor(type),
        dimmed: !(investmentTypeFilter.length === 0 || investmentTypeFilter.includes(type)),
      })),
    }).catch(error => console.error('Chart image export failed:', error));
  };

  return (
    <ChartCard ref={cardRef}>
      <ChartHeader>
        <ChartCardTitle>{title}</ChartCardTitle>
        <HeaderControls>
          <ToggleGroup>
            <ToggleButton active={mode === 'count'} onClick={() => setMode('count')}>{m.charts.count}</ToggleButton>
            <ToggleButton active={mode === 'volume'} onClick={() => setMode('volume')}>{m.charts.volume}</ToggleButton>
          </ToggleGroup>
          {rows.length > 0 && (
            <ExportButton
              type="button"
              onClick={handleExport}
              title={m.charts.exportImage}
              aria-label={m.charts.exportImage}
            >
              <span className="icon" data-icon="download" />
            </ExportButton>
          )}
        </HeaderControls>
      </ChartHeader>

      {breadcrumb && breadcrumb.length > 0 ? (
        <BreadcrumbRow aria-label={m.charts.breadcrumbAria}>
          {breadcrumb.map((seg, i) => (
            <span key={`${i}-${seg.label}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {i > 0 && <CrumbSep>›</CrumbSep>}
              {seg.onClick
                ? <CrumbLink type="button" onClick={seg.onClick}>{formatLabel(seg.label)}</CrumbLink>
                : <CrumbCurrent>{formatLabel(seg.label)}</CrumbCurrent>}
            </span>
          ))}
        </BreadcrumbRow>
      ) : hint ? (
        <Hint>{hint}</Hint>
      ) : null}

      {rows.length === 0 ? (
        <EmptyState>{m.charts.noData}</EmptyState>
      ) : (<>
        <Rows>
          {rows.map(row => {
            const v = metricVal(row);
            const dimmed = selected != null && selected !== row.label;
            const segs = row.byType
              .map(b => ({ type: b.type, value: mode === 'volume' ? b.volume : b.count }))
              .filter(b => b.value > 0)
              .sort((a, b) => typeOrder(a.type) - typeOrder(b.type));
            return (
              <Row
                key={row.label}
                dimmed={dimmed}
                highlighted={selected === row.label}
                onClick={() => handleSelect(row)}
                onMouseMove={e => setTip({ x: e.clientX, y: e.clientY, row })}
                onMouseLeave={() => setTip(null)}
              >
                <RowLabel title={formatLabel(row.label)}>
                  <RowLabelText>{formatLabel(row.label)}</RowLabelText>
                  {drillableLabels?.has(row.label) && <RowChevron aria-hidden="true">›</RowChevron>}
                </RowLabel>
                <Track>
                  <BarFill style={{ width: `${max > 0 ? (v / max) * 100 : 0}%` }}>
                    {segs.map(s => (
                      <Seg key={s.type} style={{ width: `${v > 0 ? (s.value / v) * 100 : 0}%`, background: getTypeColor(s.type) }} />
                    ))}
                  </BarFill>
                </Track>
                <RowValue>{fmt(v)}</RowValue>
              </Row>
            );
          })}
        </Rows>

        {tip && (() => {
          const items = tip.row.byType
            .map(b => ({ type: b.type, value: mode === 'volume' ? b.volume : b.count }))
            .filter(b => b.value > 0)
            .sort((a, b) => b.value - a.value);
          return (
            <Tooltip style={{ left: tip.x, top: tip.y }}>
              <div style={tipTitle}>
                {formatLabel(tip.row.label)}
              </div>
              {items.map(it => (
                <div key={it.type} style={tipRow}>
                  <span style={tipName}>
                    <span style={{ ...tipDot, background: getTypeColor(it.type) }} />
                    {typeLabel(it.type)}
                  </span>
                  <span style={{ fontWeight: theme.fontWeight.medium }}>{fmt(it.value)}</span>
                </div>
              ))}
            </Tooltip>
          );
        })()}

        <TypeLegend>
          {legendTypes.map(type => {
            const isActive = investmentTypeFilter.length === 0 || investmentTypeFilter.includes(type);
            return (
              <TypeLegendItem
                key={type}
                dimmed={!isActive}
                onClick={() => {
                  const tierTypes = ALL_INVESTMENT_TYPES.filter(t =>
                    (activeTiers.physical && PHYSICAL_TYPES.includes(t)) || (activeTiers.financial && FINANCIAL_TYPES.includes(t))
                  );
                  const allActive = investmentTypeFilter.length === 0 || tierTypes.every(t => investmentTypeFilter.includes(t));
                  if (allActive) {
                    onTypeFilterChange([type]);
                  } else if (investmentTypeFilter.includes(type)) {
                    const next = investmentTypeFilter.filter(t => t !== type);
                    onTypeFilterChange(next.length > 0 ? next : tierTypes);
                  } else {
                    const next = [...investmentTypeFilter, type];
                    const allBack = tierTypes.every(t => next.includes(t));
                    onTypeFilterChange(allBack ? tierTypes : next);
                  }
                }}
              >
                <TypeLegendDot color={getTypeColor(type)} />
                {typeLabel(type)}
              </TypeLegendItem>
            );
          })}
        </TypeLegend>
      </>)}
    </ChartCard>
  );
});
